import React, { useState } from 'react'
import { View, StyleSheet } from 'react-native'
import { Modal, Portal, Text, Button, List } from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import {
  ClusterMember,
  useRemoveClusterMemberMutation,
  useTransferClusterOwnershipMutation,
  useUpdateClusterMemberMutation,
} from '../../../../../generated/graphql'
import Toast from 'react-native-root-toast'
import * as Sentry from '@sentry/react-native'
import { useTheme } from '../../../../../hooks/theme.hook'

interface MemberActionsModalProps {
  visible: boolean
  onClose: () => void
  clusterId: string
  member: ClusterMember
  onTransferSuccess: () => void
  onDeleteSuccess: () => void
}

export const MemberActionsModal = ({
  visible,
  onClose,
  clusterId,
  member,
  onTransferSuccess,
  onDeleteSuccess,
}: MemberActionsModalProps) => {
  const { t } = useTranslation('clusters')
  const theme = useTheme()
  const [confirmAction, setConfirmAction] = useState<
    'transfer' | 'remove' | 'toggleModerator' | null
  >(null)

  const [doTransferOwnership, { loading: transferring }] =
    useTransferClusterOwnershipMutation({
      refetchQueries: ['clusterById', 'getMembersByClusterId'],
    })

  const [doRemoveMember, { loading: removing }] =
    useRemoveClusterMemberMutation({
      refetchQueries: ['getMembersByClusterId'],
    })

  const [doUpdateMember, { loading: updating }] =
    useUpdateClusterMemberMutation({
      refetchQueries: ['getMembersByClusterId'],
    })

  const handleToggleModerator = async () => {
    try {
      await doUpdateMember({
        variables: {
          input: {
            id: member.id,
            isModerator: !member.isModerator,
          },
        },
      })
      Toast.show(
        member.isModerator
          ? t('moderator-removed-successfully')
          : t('moderator-added-successfully')
      )
      setConfirmAction(null)
      onDeleteSuccess()
    } catch (error) {
      console.error('Error updating member role:', error)
      Sentry.captureException(error)
      Toast.show((error as Error).message)
    }
  }

  const handleTransferOwnership = async () => {
    try {
      await doTransferOwnership({
        variables: {
          input: {
            clusterId,
            newOwnerId: member.userId,
          },
        },
      })
      Toast.show(t('ownership-transferred-successfully'))
      setConfirmAction(null)
      onTransferSuccess()
    } catch (error) {
      console.error('Error transferring ownership:', error)
      Sentry.captureException(error)
      Toast.show((error as Error).message)
    }
  }

  const handleRemoveMember = async () => {
    try {
      await doRemoveMember({
        variables: {
          id: member.id,
        },
      })
      Toast.show(t('member-removed-successfully'))
      setConfirmAction(null)
      onDeleteSuccess()
    } catch (error) {
      console.error('Error removing member:', error)
      Sentry.captureException(error)
      Toast.show((error as Error).message)
    }
  }

  if (confirmAction === 'toggleModerator') {
    return (
      <Portal>
        <Modal
          visible={visible}
          onDismiss={onClose}
          contentContainerStyle={[
            styles.modalContainer,
            { backgroundColor: theme.colors.surface },
          ]}
        >
          <Text variant="titleLarge" style={styles.title}>
            {member.isModerator ? t('remove-moderator') : t('make-moderator')}
          </Text>
          <Text variant="bodyMedium" style={styles.description}>
            {member.isModerator
              ? t('remove-moderator-confirm', {
                  username: member.user.username,
                })
              : t('make-moderator-confirm', { username: member.user.username })}
          </Text>
          <View style={styles.buttonContainer}>
            <Button
              onPress={() => {
                setConfirmAction(null)
                onClose()
              }}
              disabled={updating}
            >
              {t('cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={handleToggleModerator}
              loading={updating}
              disabled={updating}
            >
              {member.isModerator ? t('remove') : t('promote')}
            </Button>
          </View>
        </Modal>
      </Portal>
    )
  }

  if (confirmAction === 'transfer') {
    return (
      <Portal>
        <Modal
          visible={visible}
          onDismiss={onClose}
          contentContainerStyle={[
            styles.modalContainer,
            { backgroundColor: theme.colors.surface },
          ]}
        >
          <Text variant="titleLarge" style={styles.title}>
            {t('transfer-ownership')}
          </Text>
          <Text variant="bodyMedium" style={styles.description}>
            {t('transfer-ownership-confirm', {
              username: member.user.username,
            })}
          </Text>
          <Text variant="bodySmall" style={styles.warning}>
            {t('transfer-ownership-warning')}
          </Text>
          <View style={styles.buttonContainer}>
            <Button
              onPress={() => {
                setConfirmAction(null)
                onClose()
              }}
              disabled={transferring}
            >
              {t('Cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={handleTransferOwnership}
              loading={transferring}
              disabled={transferring}
            >
              {t('Transfer')}
            </Button>
          </View>
        </Modal>
      </Portal>
    )
  }

  if (confirmAction === 'remove') {
    return (
      <Portal>
        <Modal
          visible={visible}
          onDismiss={onClose}
          contentContainerStyle={[
            styles.modalContainer,
            { backgroundColor: theme.colors.surface },
          ]}
        >
          <Text variant="titleLarge" style={styles.title}>
            {t('remove-member')}
          </Text>
          <Text variant="bodyMedium" style={styles.description}>
            {t('remove-member-confirm', { username: member.user.username })}
          </Text>
          <View style={styles.buttonContainer}>
            <Button
              onPress={() => {
                setConfirmAction(null)
                onClose()
              }}
              disabled={removing}
            >
              {t('Cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={handleRemoveMember}
              loading={removing}
              disabled={removing}
              buttonColor={theme.colors.error}
            >
              {t('Remove')}
            </Button>
          </View>
        </Modal>
      </Portal>
    )
  }

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onClose}
        contentContainerStyle={[
          styles.modalContainer,
          { backgroundColor: theme.colors.surface },
        ]}
      >
        <Text variant="titleLarge" style={styles.title}>
          {t('member-actions')}
        </Text>
        <List.Item
          title={
            member.isModerator ? t('remove-moderator') : t('make-moderator')
          }
          description={
            member.isModerator
              ? t('remove-moderator-description')
              : t('make-moderator-description')
          }
          left={(props) => (
            <List.Icon
              {...props}
              icon={
                member.isModerator ? 'account-arrow-down' : 'account-arrow-up'
              }
            />
          )}
          onPress={() => setConfirmAction('toggleModerator')}
        />
        <List.Item
          title={t('transfer-ownership')}
          description={t('transfer-ownership-description')}
          left={(props) => <List.Icon {...props} icon="account-arrow-right" />}
          onPress={() => setConfirmAction('transfer')}
        />
        <List.Item
          title={t('remove-member')}
          description={t('remove-member-description')}
          left={(props) => <List.Icon {...props} icon="account-remove" />}
          onPress={() => setConfirmAction('remove')}
        />
        <View style={styles.buttonContainer}>
          <Button onPress={onClose} mode="text">
            {t('cancel')}
          </Button>
        </View>
      </Modal>
    </Portal>
  )
}

const styles = StyleSheet.create({
  modalContainer: {
    margin: 20,
    padding: 20,
    borderRadius: 8,
  },
  title: {
    marginBottom: 16,
  },
  description: {
    marginBottom: 8,
  },
  warning: {
    marginBottom: 16,
    fontStyle: 'italic',
    opacity: 0.7,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 16,
  },
})
